import React, { useState } from "react"
import toast from "react-hot-toast"
import { X } from "lucide-react"
import { trpc } from "../../../lib/trpc"

interface ConnectAccountModalProps {
  onClose: () => void
  onSuccess: () => void
  preselectedCompanyId?: string
}

export function ConnectAccountModal({ onClose, onSuccess, preselectedCompanyId }: ConnectAccountModalProps) {
  const [companyId, setCompanyId] = useState(preselectedCompanyId || "")
  const [accountId, setAccountId] = useState("")
  const [accessToken, setAccessToken] = useState("")
  const [accountName, setAccountName] = useState("")

  // Fetch companies for dropdown
  const { data: companiesData } = trpc.companies.list.useQuery({ status: "active" })
  const companies = companiesData?.companies || []

  const addAccountMutation = trpc.metaAds.addAccount.useMutation({
    onSuccess: (data) => {
      toast.success(`Successfully added Meta ad account!`)
      onSuccess()
    },
    onError: (error) => {
      toast.error(`Failed to add account: ${error.message}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!companyId) {
      toast.error("Please select a company")
      return
    }

    if (!accountId.trim() || !accessToken.trim()) {
      toast.error("Please fill in both Account ID and Access Token")
      return
    }

    addAccountMutation.mutate({
      companyId,
      accountId: accountId.trim(),
      accessToken: accessToken.trim(),
      accountName: accountName.trim() || undefined,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border border-border max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-card-foreground">Add Meta Ads Account</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-card-foreground mb-2">Connect Your Meta Ad Account</h3>
            <p className="text-sm text-muted-foreground">
              Add your Meta ad account credentials to access campaign data and performance metrics.
            </p>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 mb-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                Company *
              </label>
              <select
                id="company"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
                required
                disabled={!!preselectedCompanyId}
              >
                <option value="">Select a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Which company does this Meta account belong to?</p>
            </div>

            <div>
              <label htmlFor="accountId" className="block text-sm font-medium text-foreground mb-2">
                Account ID *
              </label>
              <input
                type="text"
                id="accountId"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="act_1234567890"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Find this in Meta Business Manager (includes &quot;act_&quot; prefix)
              </p>
            </div>

            <div>
              <label htmlFor="accessToken" className="block text-sm font-medium text-foreground mb-2">
                Access Token *
              </label>
              <textarea
                id="accessToken"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="EAAN8wNakIM0BO4h2VwUb5Q2fLuB1..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">Long-lived access token from Meta Developer Console</p>
            </div>

            <div>
              <label htmlFor="accountName" className="block text-sm font-medium text-foreground mb-2">
                Account Name (Optional)
              </label>
              <input
                type="text"
                id="accountName"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="My Ad Account"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">Display name for this account</p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={addAccountMutation.isLoading}
            className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addAccountMutation.isLoading ? "Adding Account..." : "Add Account"}
          </button>

          {/* Help Text */}
          <div className="mt-4 bg-muted/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-foreground mb-2">How to get your credentials:</h4>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Go to Meta Business Manager</li>
              <li>Find your Account ID in Ad Account settings</li>
              <li>Generate an access token in Meta Developer Console</li>
              <li>Use a long-lived token (60+ days expiry)</li>
            </ol>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 bg-muted/30 rounded-b-lg">
          <p className="text-xs text-muted-foreground">
            Your credentials are securely stored. You can remove accounts at any time from your settings.
          </p>
        </div>
      </div>
    </div>
  )
}
